import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import AmmoSaleList, { ammoLinesOf, linesAmount } from './list';
import AmmoTrendChart from './trendChart';
import { isRevenueReason } from '../../lib/ammoReasons';
import { myPurchasesTrendRows } from '../../lib/ammoStats';

function formatAmount(value) {
    return new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 0 }).format(Number(value) || 0);
}

/**
 * Per-caliber totals of a member's own purchases: units, rounds and gross amount,
 * for the current year and all time. Internal reasons (sighting-in etc.) count
 * units and rounds but no amount, since nothing was paid.
 */
export function summarizeMyPurchases(sales = [], now = new Date()) {
    const year = now.getFullYear();
    const byCaliber = {};
    sales.forEach(sale => {
        const saleDate = sale.saleDate ? new Date(sale.saleDate) : null;
        const inYear = !!saleDate && !Number.isNaN(saleDate.getTime()) && saleDate.getFullYear() === year;
        const revenue = isRevenueReason(sale.ammoReason);
        ammoLinesOf(sale).forEach(line => {
            const caliber = line.caliber || line.name || '';
            if (!byCaliber[caliber]) {
                byCaliber[caliber] = {
                    caliber, unitsYear: 0, roundsYear: 0, amountYear: 0, unitsTotal: 0, roundsTotal: 0, amountTotal: 0,
                };
            }
            const row = byCaliber[caliber];
            const qty = Number(line.qty) || 0;
            const rounds = qty * (Number(line.roundsPerUnit) || 0);
            const amount = revenue ? linesAmount([line]) : 0;
            row.unitsTotal += qty;
            row.roundsTotal += rounds;
            row.amountTotal += amount;
            if (inYear) {
                row.unitsYear += qty;
                row.roundsYear += rounds;
                row.amountYear += amount;
            }
        });
    });
    return Object.values(byCaliber).sort((a, b) => a.caliber.localeCompare(b.caliber));
}

@observer
class AmmoMyPurchases extends Component {
    componentDidMount() {
        const { ammoSaleStore } = this.props.stores;
        ammoSaleStore.loadMine();
    }

    render() {
        const { ammoSaleStore, appState } = this.props.stores;
        const { myPurchases = [] } = ammoSaleStore;
        const summary = summarizeMyPurchases(myPurchases);
        const trendRows = myPurchasesTrendRows(myPurchases);
        return (
            <div class='w-100'>
                <h6 class='mb-2'>
                    <i class='fa-solid fa-box-open me-2' />
                    <Text id='ammoSale.my-purchases'>My ammo purchases</Text>
                </h6>
                {myPurchases.length === 0 ? (
                    <div class='text-body-secondary py-3'>
                        <Text id='ammoSale.my-empty'>You have no registered ammo purchases.</Text>
                    </div>
                ) : (
                    <div>
                        <AmmoTrendChart rows={trendRows} isMobile={!!appState?.isMobile} />
                        <div class='table-responsive mb-3'>
                            <table class='table table-sm mb-0 ammo-my-summary'>
                                <thead>
                                    <tr>
                                        <th><Text id='ammoSale.caliber'>Caliber</Text></th>
                                        <th class='text-end'><Text id='ammoSale.my-units'>Units</Text></th>
                                        <th class='text-end'><Text id='ammoSale.my-rounds'>Rounds</Text></th>
                                        <th class='text-end'><Text id='ammoSale.my-amount'>Amount</Text></th>
                                    </tr>
                                    <tr>
                                        <th />
                                        <th class='text-end fw-normal text-body-secondary small' colSpan='3'>
                                            <Text id='ammoSale.my-year-vs-total'>this year / total</Text>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {summary.map(row => (
                                        <tr key={row.caliber}>
                                            <td>{row.caliber}</td>
                                            <td class='text-end text-nowrap'>{row.unitsYear} / {row.unitsTotal}</td>
                                            <td class='text-end text-nowrap'>{row.roundsYear} / {row.roundsTotal}</td>
                                            <td class='text-end text-nowrap'>{formatAmount(row.amountYear)} / {formatAmount(row.amountTotal)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <AmmoSaleList sales={myPurchases} canEdit={false} onEdit={() => {}} />
                    </div>
                )}
            </div>
        );
    }
}

export default AmmoMyPurchases;
